import { useAxios } from '../useAxios';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createProject,
  createSavedTab,
  deleteSavedTab,
  deleteProject,
  getProject,
  getProjectEmails,
  getProjects,
  importCsvToProject,
  setDefaultCriteria,
  updateProject,
  attachCvToCandidate,
  addFromCriteria,
  getScrapeStatus,
  approveProject,
  migrateProjects,
  migrateProfiles,
  exportJsonFromProject,
  resourceIds,
  updateProjectStatus
} from '../../services/api/projects';
import config from '../../config';
import {
  CategoryDataType,
  CsvExportOptionsEnum,
  EProjectStatus,
  IImportCsv,
  IProject,
  RefreshTypeEnum
} from '@recone/models';
import { useGraphQlRequest } from '../useGraphQLClient';
import { LinkedinUsersSearchInputType, useGetFilterProjectQuery } from '../../gql/generated/graphql';
import { GET_CRITERIAS_QUERY_ID } from './criteria';
import {
  addNotesToProfile,
  assignFilteredToSavedTab,
  assignToSavedTab,
  copyAllToProject,
  removeFromProject
} from 'services/api/team-profile';
import { IProjectFromFiltersInputType } from 'components/SearchCriteria/SearchCriteria';
import useTeamStore from 'store/useTeamStore';

const GET_PROJECTS_QUERY_ID = 'projects';
const GET_TARGETS_QUERY_ID = 'targets';

const GET_PROJECT_RESOURCES_LINKEDIN_IDS = 'linkedin_ids';

export function useGetProjects(limit = config.service.QUERY_DEFAULT_LIMIT, status: EProjectStatus) {
  const axios = useAxios();
  const teamId = useTeamStore((state) => state.teamId);

  return useInfiniteQuery(
    [GET_PROJECTS_QUERY_ID, status, teamId],
    ({ pageParam }) => getProjects(axios, pageParam, limit, status),
    {
      enabled: !!status,
      getNextPageParam: (lastData, pages) => pages.length
    }
  );
}

export function useCreateProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { projectName: string; description: string; candidatesNumber: number; status?: EProjectStatus }) =>
      createProject(axios, args.projectName, args.description, args.candidatesNumber, args.status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
      }
    }
  );
}

export function useAddFromCriteria() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: IProjectFromFiltersInputType) => addFromCriteria(axios, args), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
    }
  });
}

export function useUpdateProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { project: Partial<IProject> }) => updateProject(axios, args.project), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
    }
  });
}

export function useDeleteProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { projectId: string }) => deleteProject(axios, args.projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
    }
  });
}

export function useCreateSavedTabs(projectId: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { projectId: string; name: string }) => createSavedTab(axios, args.projectId, args.name), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID, projectId]).then();
    }
  });
}

export function useDeleteSavedTab(projectId: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { projectId: string; savedTabId: string }) => deleteSavedTab(axios, args.projectId, args.savedTabId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID, projectId]).then();
      }
    }
  );
}

export function useGetProject(projectId: string) {
  const axios = useAxios();

  return useQuery([GET_PROJECTS_QUERY_ID, projectId], () => getProject(axios, projectId), {
    enabled: !!projectId
  });
}

export function useImportCsvToProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { payload: IImportCsv[]; projectId: string }) => importCsvToProject(axios, args.payload, args.projectId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
}

export function useExportJsonFromProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { id: string; exportOptions: CsvExportOptionsEnum }) =>
      exportJsonFromProject(axios, args.id, args.exportOptions),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
      }
    }
  );
}

export function useGetEmailsForProject() {
  const axios = useAxios();

  return useMutation((args: { projectId: string }) => getProjectEmails(axios, args.projectId));
}

export function useGetProjectFiltered(filter: LinkedinUsersSearchInputType, limit = 25, skip = 0) {
  const graphqlRequest = useGraphQlRequest();

  return useGetFilterProjectQuery(graphqlRequest, { filter, limit, skip });
}

export function useSetDefaultCriteria(projectId: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { criteriaId: string | null }) => setDefaultCriteria(axios, projectId, args.criteriaId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID, projectId]).then();
      queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      queryClient.invalidateQueries([GET_CRITERIAS_QUERY_ID]).then();
    }
  });
}

export function useAttachCVToCandidate() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((formData: FormData) => attachCvToCandidate(axios, formData), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
    }
  });
}

export function useAddToSavedTab() {
  const axiosInstance = useAxios();
  const queryClient = useQueryClient();
  return useMutation(
    (args: { accountIds: string | string[]; projectId: string }) =>
      assignToSavedTab(axiosInstance, args.accountIds, args.projectId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
}

export function useAddFilteredToSavedTab() {
  const axiosInstance = useAxios();
  const queryClient = useQueryClient();
  return useMutation(
    (args: { filters: CategoryDataType[]; sourceProjectId: string; targetProjectId: string }) =>
      assignFilteredToSavedTab(axiosInstance, args.filters, args.sourceProjectId, args.targetProjectId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
}

export function useRemoveFromSavedTab() {
  const axiosInstance = useAxios();
  const queryClient = useQueryClient();
  return useMutation(
    (args: { projectId: string; ids: string[] }) => removeFromProject(axiosInstance, args.projectId, args.ids),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
}

export function useGetScrapeStatus(projectId: string, { enabled = false } = {}) {
  const axios = useAxios();

  return useQuery([GET_PROJECTS_QUERY_ID, projectId], () => getScrapeStatus(axios, projectId), {
    enabled: !!projectId && enabled
  });
}

export function useApproveProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { projectId: string; force?: boolean }) => approveProject(axios, args.projectId, args?.force),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_CRITERIAS_QUERY_ID]).then();
      }
    }
  );
}

export function useAddNotesToProfile() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation(
    (args: { profileId: string; projectId: string; notes: string }) =>
      addNotesToProfile(axios, args.profileId, args.projectId, args.notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
}

export function useMigrateProjects() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { projectIds: string[]; sourceTeamId: string; targetTeamId: string; move?: boolean }) =>
      migrateProjects(axios, args.projectIds, args.sourceTeamId, args.targetTeamId, args.move),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
      }
    }
  );
}

export function useMigrateLeads() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { sourceProjectIds: string[]; targetProjectId: string; move?: boolean; deleteOnMove?: boolean }) =>
      migrateProfiles(axios, args.sourceProjectIds, args.targetProjectId, args.move, args.deleteOnMove),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
      }
    }
  );
}

export const useCopyLeads = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { sourceProjectId: string; targetProjectId: string }) =>
      copyAllToProject(axios, args.sourceProjectId, args.targetProjectId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_TARGETS_QUERY_ID]).then();
      }
    }
  );
};

export function useGetResourceLinkedinIds(projectId: string, resource: RefreshTypeEnum) {
  const axios = useAxios();

  return useQuery(
    [GET_PROJECT_RESOURCES_LINKEDIN_IDS, projectId, resource],
    () => resourceIds(axios, projectId, resource),
    {
      enabled: !!projectId
    }
  );
}

export const useUpdateProjectStatus = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation(
    (args: { status: EProjectStatus; projectId: string }) => updateProjectStatus(axios, args.projectId, args.status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY_ID]).then();
      }
    }
  );
};
